import {
  Button,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from '@ibsys/kit';
import { FC, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  /** Кнопка закрепления, обёрнутая в `DialogTrigger`: окно открывается по ней */
  trigger: ReactNode;
  /** Название пресета — для заголовка предупреждения */
  presetName: string;
  /** Донастройку можно записать в пресет; иначе предлагается только закрепить без изменений */
  canSaveChanges: boolean;
  /** Закрепить пресет в сохранённом виде */
  onPinWithoutChanges: () => void;
  /** Записать донастройку в пресет и закрепить его */
  onSaveAndPin: () => void;
};

/**
 * Предупреждение при закреплении пресета с несохранёнными изменениями.
 *
 * Раздел закреплённого пресета начинается с его сохранённого вида, поэтому молчаливое закрепление
 * теряло бы донастройку. Окно оставляет выбор пользователю: «Сохранить и закрепить» или
 * «Закрепить без изменений»; закрытие окна без выбора ничего не меняет. Записать донастройку
 * в пресет, которого пользователь не правит (получатель совместного пресета), окно не предлагает.
 */
export const PinUnsavedPresetDialog: FC<Props> = ({
  trigger,
  presetName,
  canSaveChanges,
  onPinWithoutChanges,
  onSaveAndPin,
}) => {
  const { t } = useTranslation('tablePresets');
  const [open, setOpen] = useState(false);

  const handlePinWithoutChanges = () => {
    setOpen(false);
    onPinWithoutChanges();
  };

  const handleSaveAndPin = () => {
    setOpen(false);
    onSaveAndPin();
  };

  return (
    <DialogRoot open={open} onOpenChange={setOpen}>
      {trigger}
      <DialogContent className="sm:max-w-xl">
        <DialogHeader className="min-w-0">
          {/* Крестик окна стоит поверх содержимого: место под него оставляет сам заголовок */}
          <DialogTitle className="pr-6 wrap-anywhere">
            <span className="leading-snug">{t('pinUnsavedTitle', { name: presetName })}</span>
          </DialogTitle>
        </DialogHeader>
        <DialogDescription>
          {canSaveChanges ? t('pinUnsavedDescription') : t('pinUnsavedReadOnlyDescription')}
        </DialogDescription>
        <DialogFooter className="flex-wrap">
          <Button variant={canSaveChanges ? 'secondary' : 'default'} onClick={handlePinWithoutChanges}>
            {t('pinWithoutChanges')}
          </Button>
          {canSaveChanges && <Button onClick={handleSaveAndPin}>{t('saveAndPin')}</Button>}
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};
